import { useState, useRef, useEffect, useCallback } from "react";
import { Slider } from "./ui/slider";

const FREQUENCY_LABELS = {
  250: "250 Hz",
  500: "500 Hz",
  1000: "1 kHz",
  2000: "2 kHz",
  4000: "4 kHz",
  8000: "8 kHz",
};

// Custom hook for audio tone playback
function useAudioTone(isLeftEar) {
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainNodeRef = useRef(null);

  const stopTone = useCallback(() => {
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
      } catch {
        // Oscillator already stopped, safe to ignore
      }
      oscillatorRef.current = null;
    }
  }, []);

  const playTone = useCallback((frequency, dbLevel) => {
    stopTone();

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const audioContext = audioContextRef.current;
    
    oscillatorRef.current = audioContext.createOscillator();
    gainNodeRef.current = audioContext.createGain();
    
    // Create stereo panner for left/right ear
    const pannerNode = audioContext.createStereoPanner();
    pannerNode.pan.value = isLeftEar ? -1 : 1;
    
    oscillatorRef.current.type = "sine";
    oscillatorRef.current.frequency.value = frequency;
    
    // Convert dB to gain (0-100 dB scale)
    const normalizedDb = dbLevel / 100;
    const gain = Math.pow(10, -(normalizedDb * 4));
    gainNodeRef.current.gain.value = gain * 0.5;
    
    oscillatorRef.current.connect(gainNodeRef.current);
    gainNodeRef.current.connect(pannerNode);
    pannerNode.connect(audioContext.destination);
    
    oscillatorRef.current.start();
  }, [isLeftEar, stopTone]);

  const cleanup = useCallback(() => {
    stopTone();
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, [stopTone]);

  return { playTone, stopTone, cleanup };
}

// Extracted component for individual frequency slider
function FrequencySlider({ 
  frequency, 
  index, 
  dbLevel, 
  isActive, 
  accentColor, 
  onSliderChange, 
  onSliderCommit,
  onDecrement,
  onIncrement 
}) {
  return (
    <div className="flex flex-col items-center">
      <p className="text-[#64748B] text-xs sm:text-sm font-medium mb-3 text-center">
        {FREQUENCY_LABELS[frequency]}
      </p>

      <div className="relative h-48 sm:h-64 flex flex-col items-center">
        {/* dB scale markers */}
        <div className="absolute left-0 top-0 bottom-0 w-px bg-[#1E293B] -ml-3 hidden sm:block">
          {[0, 25, 50, 75, 100].map((mark) => (
            <div
              key={mark}
              className="absolute w-2 h-px bg-[#334155] -ml-1"
              style={{ top: `${mark}%` }}
            />
          ))}
        </div>

        {/* Vertical Slider */}
        <div className="h-full flex items-center">
          <Slider
            orientation="vertical"
            min={0}
            max={100}
            step={5}
            value={[dbLevel]}
            onValueChange={(value) => onSliderChange(index, value)}
            onValueCommit={onSliderCommit}
            className="h-full"
            style={{
              "--slider-track-bg": "#1E293B",
              "--slider-range-bg": accentColor,
              "--slider-thumb-bg": "#FFFFFF",
              "--slider-thumb-border": accentColor,
            }}
            data-testid={`slider-${frequency}`}
          />
        </div>
      </div>

      {/* +/- Buttons */}
      <div className="flex items-center gap-1 mt-3">
        <button
          onClick={() => onDecrement(index)}
          className="w-8 h-8 rounded-lg bg-[#1E293B] text-[#94A3B8] hover:bg-[#334155] hover:text-[#F8FAFC] transition-colors flex items-center justify-center text-lg font-bold"
          data-testid={`slider-${frequency}-minus`}
        >
          -
        </button>
        <button
          onClick={() => onIncrement(index)}
          className="w-8 h-8 rounded-lg bg-[#1E293B] text-[#94A3B8] hover:bg-[#334155] hover:text-[#F8FAFC] transition-colors flex items-center justify-center text-lg font-bold"
          data-testid={`slider-${frequency}-plus`}
        >
          +
        </button>
      </div>

      {/* Current dB display */}
      <p
        className="text-lg font-bold mt-2 font-['Outfit']"
        style={{ color: isActive ? accentColor : "#94A3B8" }}
      >
        {dbLevel} dB
      </p>
    </div>
  );
}

export default function FrequencyTest({ ear, frequencies, onComplete, onBack }) {
  const [results, setResults] = useState(
    frequencies.map((freq) => ({ frequency: freq, db_level: 50 }))
  );
  const [activeFrequencyIndex, setActiveFrequencyIndex] = useState(null);

  const isLeftEar = ear === "left";
  const accentColor = isLeftEar ? "#22D3EE" : "#60A5FA";

  const { playTone, stopTone, cleanup } = useAudioTone(isLeftEar);

  // Cleanup audio on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleSliderChange = useCallback((index, value) => {
    setResults((prevResults) => {
      const newResults = [...prevResults];
      newResults[index] = { ...newResults[index], db_level: value[0] };
      return newResults;
    });
    
    playTone(frequencies[index], value[0]);
    setActiveFrequencyIndex(index);
  }, [frequencies, playTone]);

  const handleSliderCommit = useCallback(() => {
    stopTone();
    setActiveFrequencyIndex(null);
  }, [stopTone]);

  const handleDecrement = useCallback((index) => {
    const newValue = Math.max(0, results[index].db_level - 5);
    handleSliderChange(index, [newValue]);
    setTimeout(handleSliderCommit, 200);
  }, [results, handleSliderChange, handleSliderCommit]);

  const handleIncrement = useCallback((index) => {
    const newValue = Math.min(100, results[index].db_level + 5);
    handleSliderChange(index, [newValue]);
    setTimeout(handleSliderCommit, 200);
  }, [results, handleSliderChange, handleSliderCommit]);

  const handleComplete = useCallback(() => {
    stopTone();
    onComplete(results);
  }, [stopTone, onComplete, results]);

  const handleBack = useCallback(() => {
    stopTone();
    onBack();
  }, [stopTone, onBack]);

  return (
    <div className="bg-[#111827] border border-[#1E2D3D] rounded-2xl p-6 sm:p-8 max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: accentColor }}
          />
          <span
            className="text-sm font-semibold uppercase tracking-wider"
            style={{ color: accentColor }}
          >
            Testing {ear} Ear
          </span>
        </div>

        <h2 className="text-2xl sm:text-3xl font-medium text-[#F8FAFC] mb-3 font-['Outfit']">
          Move each slider to the quietest sound you can hear
        </h2>

        <p className="text-[#94A3B8] max-w-lg mx-auto">
          Drag each slider down until you can barely hear the tone.
          Use the + and - buttons for fine adjustments.
        </p>
      </div>

      {/* Frequency Labels */}
      <div className="flex items-center justify-between mb-2 px-2">
        <span className="text-[#64748B] text-sm">Quieter</span>
        <span className="text-[#64748B] text-sm">Louder</span>
      </div>

      {/* Sliders Grid */}
      <div className="grid grid-cols-6 gap-4 sm:gap-6 mb-8">
        {frequencies.map((freq, index) => (
          <FrequencySlider
            key={freq}
            frequency={freq}
            index={index}
            dbLevel={results[index].db_level}
            isActive={activeFrequencyIndex === index}
            accentColor={accentColor}
            onSliderChange={handleSliderChange}
            onSliderCommit={handleSliderCommit}
            onDecrement={handleDecrement}
            onIncrement={handleIncrement}
          />
        ))}
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleBack}
          className="sm:flex-1 py-3 px-6 rounded-lg border border-[#334155] text-[#94A3B8] font-medium hover:bg-[#1E293B] hover:text-[#F8FAFC] transition-colors flex items-center justify-center gap-2"
          data-testid={`${ear}-ear-back-btn`}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>

        <button
          onClick={handleComplete}
          className="sm:flex-[2] py-4 px-6 bg-[#22D3EE] text-[#0A0F1C] font-semibold rounded-lg hover:bg-[#67E8F9] transition-all flex items-center justify-center gap-2"
          data-testid={`${ear}-ear-complete-btn`}
        >
          {ear === "left" ? (
            <>
              Continue to Right Ear
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              Analyze Results
            </>
          )}
        </button>
      </div>
    </div>
  );
}
